import type { ReactNode, Ref } from 'react'
import type { Checkbox as CheckboxNS } from '@base-ui/react/checkbox'
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox'

import { CheckIcon } from '@gto/icons-react/outline/check'
import { MinusIcon } from '@gto/icons-react/outline/minus'
import { cn } from '@gto/ui/cn'

import { checkboxVariants } from './checkbox.styles'

type CheckboxColor = 'default' | 'primary'
type CheckboxSize = 'small' | 'medium' | 'large'
type CheckboxLabelPlacement = 'end' | 'start' | 'top' | 'bottom'

type CheckboxOwnProps = {
  /** Whether the checkbox is checked (controlled) */
  checked?: boolean
  /** Callback when the checked state changes */
  onCheckedChange?: CheckboxNS.Root.Props['onCheckedChange']
  /** Whether the checkbox is in an indeterminate state */
  indeterminate?: boolean
  /** Whether the checkbox is disabled */
  disabled?: boolean
  /** Whether the checkbox is required — shows an asterisk next to the label */
  required?: boolean
  /** Color variant */
  color?: CheckboxColor
  /** Size variant */
  size?: CheckboxSize
  /** Form field name */
  name?: string
  /** Form field value */
  value?: string
  /** Label displayed next to the checkbox */
  label?: ReactNode
  /** Position of the label relative to the checkbox */
  labelPlacement?: CheckboxLabelPlacement
  /** Icon rendered in place of the standard checkbox box */
  icon?: ReactNode
  /** Additional CSS classes for the root */
  className?: string
  /** React 19 ref prop */
  ref?: Ref<HTMLButtonElement>
  /** Ref for the hidden input element */
  inputRef?: Ref<HTMLInputElement>
}

type CheckboxProps = CheckboxOwnProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof CheckboxOwnProps | 'onChange'>

const Checkbox = ({
  checked,
  onCheckedChange,
  indeterminate = false,
  disabled = false,
  required = false,
  color = 'primary',
  size = 'medium',
  name,
  value,
  label,
  labelPlacement = 'end',
  icon,
  className,
  ref,
  inputRef,
  ...rest
}: CheckboxProps) => {
  const styles = checkboxVariants({ size, color, labelPlacement, disabled })

  const checkboxEl =
    icon == null ? (
      <BaseCheckbox.Root
        ref={ref}
        checked={checked}
        className={cn(styles.root(), label == null && className)}
        disabled={disabled}
        indeterminate={indeterminate}
        inputRef={inputRef}
        name={name}
        value={value}
        onCheckedChange={onCheckedChange}
        {...rest}
      >
        <BaseCheckbox.Indicator className={styles.indicator()}>
          <CheckIcon className={styles.checkIcon()} />
          <MinusIcon className={styles.indeterminateIcon()} />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
    ) : (
      <BaseCheckbox.Root
        ref={ref}
        checked={checked}
        disabled={disabled}
        indeterminate={indeterminate}
        inputRef={inputRef}
        name={name}
        value={value}
        onCheckedChange={onCheckedChange}
        {...rest}
        className={cn(styles.iconRoot(), label == null && className)}
      >
        {icon}
      </BaseCheckbox.Root>
    )

  if (label == null) {
    return checkboxEl
  }

  return (
    <label className={cn(styles.label(), className)}>
      {checkboxEl}
      <span className={styles.labelText()}>
        {label}
        {required && <span className="ml-1 text-error-main">*</span>}
      </span>
    </label>
  )
}

export { Checkbox }
export type { CheckboxColor, CheckboxLabelPlacement, CheckboxProps, CheckboxSize }
